import React, { useState } from 'react';
import { ChevronDownIcon, SearchIcon } from 'lucide-react';
import { Input } from '../components/ui/Input';
const faqCategories = [{
  name: 'Orders & Shipping',
  faqs: [{
    question: 'How long does delivery take?',
    answer: 'Delivery typically takes 3-5 business days for major cities in Pakistan. Remote areas may take 5-7 business days. You can track your order using the tracking number provided in your confirmation email.'
  }, {
    question: 'Do you offer free shipping?',
    answer: 'Yes! We offer free shipping on all orders above PKR 5,000. For orders below this amount, a flat shipping fee of PKR 250-500 applies depending on your location.'
  }, {
    question: 'Can I track my order?',
    answer: "Absolutely! Once your order is shipped, you will receive a tracking number via email and SMS. You can use this to track your package on our website or the courier's website."
  }, {
    question: 'Do you deliver to all cities in Pakistan?',
    answer: 'Yes, we deliver nationwide across Pakistan. Delivery times may vary based on your location. Major cities receive faster delivery compared to remote areas.'
  }]
}, {
  name: 'Payment',
  faqs: [{
    question: 'What payment methods do you accept?',
    answer: 'We accept multiple payment methods including Cash on Delivery (COD), Credit/Debit Cards (Visa, MasterCard), and mobile wallet payments through JazzCash and EasyPaisa.'
  }, {
    question: 'Is Cash on Delivery available?',
    answer: 'Yes, Cash on Delivery (COD) is available for all orders across Pakistan. You can pay in cash when your order is delivered to your doorstep.'
  }, {
    question: 'Is online payment secure?',
    answer: 'Yes, all online payments are processed through secure, encrypted payment gateways. We use SSL encryption to protect your financial information.'
  }]
}, {
  name: 'Returns & Refunds',
  faqs: [{
    question: 'What is your return policy?',
    answer: 'We offer a 7-day return policy for all products. Items must be unused, in original packaging, and with all accessories. Contact our support team to initiate a return.'
  }, {
    question: 'How do I return a product?',
    answer: 'To return a product, contact our customer support team via email or phone. We will arrange for pickup of the item and process your refund within 5-7 business days after receiving the returned product.'
  }, {
    question: 'When will I receive my refund?',
    answer: 'Refunds are processed within 5-7 business days after we receive and inspect the returned item. The refund will be credited to your original payment method.'
  }]
}, {
  name: 'Products & Warranty',
  faqs: [{
    question: 'Are all products genuine?',
    answer: 'Yes, we guarantee 100% authentic products. All items are sourced directly from authorized distributors and come with official warranty.'
  }, {
    question: 'What warranty do products come with?',
    answer: 'Most products come with manufacturer warranty ranging from 6 months to 2 years depending on the product and brand. Warranty details are mentioned on each product page.'
  }, {
    question: 'How do I claim warranty?',
    answer: 'To claim warranty, contact our support team with your order number and proof of purchase. We will guide you through the warranty claim process with the manufacturer.'
  }]
}];
export function FAQPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const toggleItem = (id: string) => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const filteredCategories = faqCategories.map(category => ({
    ...category,
    faqs: category.faqs.filter(faq => faq.question.toLowerCase().includes(searchQuery.toLowerCase()) || faq.answer.toLowerCase().includes(searchQuery.toLowerCase()))
  })).filter(category => category.faqs.length > 0);
  return <div className="min-h-screen bg-gray-50" data-id="element-1346" data-mp="141qsbuq9s5c5">
      {/* Hero */}
      <div className="bg-primary-600 py-16" data-id="element-1347" data-mp="2xjoci80l0z6d">
        <div className="max-w-7xl mx-auto px-4 text-center" data-id="element-1348" data-mp="2xiwr1q0p8htt">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1349" data-mp="32ihq00fvcq6j">
            Frequently Asked Questions
          </h1>
          <p className="text-primary-100 max-w-2xl mx-auto mb-8" data-id="element-1350" data-mp="37aixl923dsq8">
            Find answers to common questions about orders, shipping, payments,
            and more.
          </p>
          <div className="max-w-xl mx-auto" data-id="element-1351" data-mp="2elbzgb6jv55i">
            <Input type="search" placeholder="Search for answers..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} leftIcon={<SearchIcon className="w-5 h-5" data-id="element-1353" data-mp="2oizyhdb6uyth" />} className="bg-white" data-id="element-1352" data-mp="2emn40d7tkgxo" />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12" data-id="element-1354" data-mp="2thtebcdy71wt">
        {filteredCategories.length === 0 ? <div className="text-center py-12" data-id="element-1355" data-mp="3srbuceju50h">
            <p className="text-gray-500" data-id="element-1356" data-mp="3rzgn2ov69aq">
              No results found for "{searchQuery}"
            </p>
          </div> : <div className="space-y-8" data-id="element-1357" data-mp="8q0yjkak02km">
            {filteredCategories.map(category => <div key={category.name} data-id="element-1358" data-mp="1t8v2d1d32v41">
                <h2 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-1359" data-mp="1tbpjok0svzt8">
                  {category.name}
                </h2>
                <div className="space-y-3" data-id="element-1360" data-mp="1jjjt9eq75qg5">
                  {category.faqs.map((faq, index) => {
              const itemId = `${category.name}-${index}`;
              const isOpen = openItems[itemId];
              return <div key={index} className="bg-white rounded-xl border border-gray-100 shadow-card overflow-hidden" data-id="element-1361" data-mp="19m5paelgmu4q" data-mpi={index}>
                        <button onClick={() => toggleItem(itemId)} className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors" aria-expanded={isOpen} data-id="element-1362" data-mp="15jity3pkyr2s">
                          <span className="font-medium text-gray-900 pr-4" data-id="element-1363" data-mp="vnfs6yyn4ugd">
                            {faq.question}
                          </span>
                          <ChevronDownIcon className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-1364" data-mp="2q05m3gp0e3nr" />
                        </button>
                        {isOpen && <div className="px-4 pb-4" data-id="element-1365" data-mp="o954t8jkr3hu">
                            <p className="text-gray-600" data-id="element-1366" data-mp="t2rgaxe051c2">{faq.answer}</p>
                          </div>}
                      </div>;
            })}
                </div>
              </div>)}
          </div>}

        {/* Contact CTA */}
        <div className="mt-12 bg-primary-50 rounded-xl p-8 text-center" data-id="element-1367" data-mp="jc6b2r3toyk6">
          <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-1368" data-mp="172sjwklnimv8">
            Still have questions?
          </h3>
          <p className="text-gray-600 mb-4" data-id="element-1369" data-mp="1c028jy17uui0">
            Can't find the answer you're looking for? Our support team is here
            to help.
          </p>
          <a href="/contact" className="inline-flex items-center justify-center px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors" data-id="element-1370" data-mp="121v063kztkrm">
            Contact Support
          </a>
        </div>
      </div>
    </div>;
}